import { useEffect } from "react";

const SITE = "Grofkart";
const DEFAULT_DESC = "Grofkart — Your Local Store, Now Online. Shop groceries and daily essentials from trusted stores near you, delivered in 30 minutes.";

function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, obj) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  if (!obj) return;
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.id = id;
  s.text = JSON.stringify(obj);
  document.head.appendChild(s);
}

/**
 * Imperatively manage per-page SEO (title, description, canonical, Open Graph,
 * Twitter, and optional JSON-LD structured data) for this CSR SPA.
 */
export function useSeo({ title, description, image, path, jsonLd, jsonLdId = "gf-jsonld" } = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} · ${SITE}` : `${SITE} — Your Local Store, Now Online`;
    const desc = description || DEFAULT_DESC;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const url = path ? `${origin}${path}` : (typeof window !== "undefined" ? window.location.href : "");
    const img = image || `${origin}/hero-grofkart.jpg`;

    document.title = fullTitle;
    setMeta("name", "description", desc);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", img);
    setMeta("property", "og:site_name", SITE);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", img);
    setLink("canonical", url);
    setJsonLd(jsonLdId, jsonLd);

    return () => setJsonLd(jsonLdId, null);
  }, [title, description, image, path, jsonLdId, JSON.stringify(jsonLd)]);
}

export const orgJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE,
  description: DEFAULT_DESC,
  url: typeof window !== "undefined" ? window.location.origin : "",
  logo: (typeof window !== "undefined" ? window.location.origin : "") + "/logo.png",
});

export const productJsonLd = (p) => p && ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: p.name,
  image: p.image,
  description: p.description || p.name,
  brand: { "@type": "Brand", name: p.brand || "Local" },
  offers: {
    "@type": "Offer", priceCurrency: "INR", price: p.price,
    availability: p.is_available ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    seller: { "@type": "Organization", name: p.store?.name || p.store_name || SITE },
  },
});

export const storeJsonLd = (s) => s && ({
  "@context": "https://schema.org",
  "@type": "GroceryStore",
  name: s.name,
  image: s.cover || s.logo,
  description: s.description,
  address: { "@type": "PostalAddress", addressLocality: s.area || s.city, addressRegion: s.state, addressCountry: "IN" },
  aggregateRating: s.rating ? { "@type": "AggregateRating", ratingValue: s.rating, reviewCount: s.reviews_count || 1 } : undefined,
  telephone: s.phone,
});
